"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * @since 0.0.1
 */
var fc = require("fast-check");
var Eq_1 = require("fp-ts/lib/Eq");
var laws = require("./laws");
/**
 * Tests the `Eq` laws
 *
 * @since 0.0.1
 */
exports.eq = function (E, arb) {
    var reflexivity = fc.property(arb, laws.eq.reflexivity(E));
    var symmetry = fc.property(arb, arb, laws.eq.simmetry(E));
    var transitivity = fc.property(arb, arb, arb, laws.eq.transitivity(E));
    fc.assert(reflexivity);
    fc.assert(symmetry);
    fc.assert(transitivity);
};
/**
 * Tests the `Ord` laws
 *
 * @since 0.0.1
 */
exports.ord = function (O, arb) {
    exports.eq(O, arb);
    var totality = fc.property(arb, arb, laws.ord.totality(O));
    var reflexivity = fc.property(arb, laws.ord.reflexivity(O));
    var antisymmetry = fc.property(arb, arb, laws.ord.antisimmetry(O));
    var transitivity = fc.property(arb, arb, arb, laws.ord.transitivity(O));
    fc.assert(totality);
    fc.assert(reflexivity);
    fc.assert(antisymmetry);
    fc.assert(transitivity);
};
/**
 * Tests the `Semigroup` laws
 *
 * @example
 * import * as laws from 'fp-ts-laws'
 * import * as fc from 'fast-check'
 * import { Semigroup } from 'fp-ts/lib/Semigroup'
 * import { eqString } from 'fp-ts/lib/Eq'
 *
 * const semigroupSpace: Semigroup<string> = {
 *   concat: (x, y) => x + ' ' + y
 * }
 * laws.semigroup(semigroupSpace, eqString, fc.string())
 *
 * @since 0.0.1
 */
exports.semigroup = function (S, E, arb) {
    var associativity = fc.property(arb, arb, arb, laws.semigroup.associativity(S, E));
    fc.assert(associativity);
};
/**
 * Tests the `Monoid` laws
 *
 * @since 0.0.1
 */
exports.monoid = function (M, E, arb) {
    exports.semigroup(M, E, arb);
    var rightIdentity = fc.property(arb, laws.monoid.rightIdentity(M, E));
    var leftIdentity = fc.property(arb, laws.monoid.leftIdentity(M, E));
    fc.assert(rightIdentity);
    fc.assert(leftIdentity);
};
/**
 * Tests the `Semiring` laws
 *
 * @since 0.0.1
 */
exports.semiring = function (S, E, arb, seed) {
    var addAssociativity = fc.property(arb, arb, arb, laws.semiring.addAssociativity(S, E));
    var addIdentity = fc.property(arb, laws.semiring.addIdentity(S, E));
    var commutativity = fc.property(arb, arb, laws.semiring.commutativity(S, E));
    var mulAssociativity = fc.property(arb, arb, arb, laws.semiring.mulAssociativity(S, E));
    var mulIdentity = fc.property(arb, laws.semiring.mulIdentity(S, E));
    var leftDistributivity = fc.property(arb, arb, arb, laws.semiring.leftDistributivity(S, E));
    var rightDistributivity = fc.property(arb, arb, arb, laws.semiring.rightDistributivity(S, E));
    var annihilation = fc.property(arb, laws.semiring.annihilation(S, E));
    fc.assert(addAssociativity, { seed: seed });
    fc.assert(addIdentity, { seed: seed });
    fc.assert(commutativity, { seed: seed });
    fc.assert(mulAssociativity, { seed: seed });
    fc.assert(mulIdentity, { seed: seed });
    fc.assert(leftDistributivity, { seed: seed });
    fc.assert(rightDistributivity, { seed: seed });
    fc.assert(annihilation, { seed: seed });
};
/**
 * Tests the `Ring` laws
 *
 * @since 0.0.1
 */
exports.ring = function (R, S, arb, seed) {
    exports.semiring(R, S, arb, seed);
    var additiveInverse = fc.property(arb, laws.ring.additiveInverse(R, S));
    fc.assert(additiveInverse);
};
/**
 * Tests the `Field` laws
 *
 * @since 0.0.1
 */
exports.field = function (F, S, arb, seed) {
    exports.ring(F, S, arb, seed);
    if (S.equals(F.zero, F.one)) {
        throw new Error("one should not be equal to zero");
    }
    var commutativity = fc.property(arb, arb, laws.field.commutativity(F, S));
    var integralDomain = fc.property(arb, arb, laws.field.integralDomain(F, S));
    var nonNegativity = fc.property(arb, laws.field.nonNegativity(F, S));
    var quotient = fc.property(arb, arb, laws.field.quotient(F, S));
    var reminder = fc.property(arb, arb, laws.field.reminder(F, S));
    var submultiplicative = fc.property(arb, arb, laws.field.submultiplicative(F, S));
    var inverse = fc.property(arb, laws.field.inverse(F, S));
    fc.assert(commutativity, { seed: seed });
    fc.assert(integralDomain, { seed: seed });
    fc.assert(nonNegativity, { seed: seed });
    fc.assert(quotient, { seed: seed });
    fc.assert(reminder, { seed: seed });
    fc.assert(submultiplicative, { seed: seed });
    fc.assert(inverse, { seed: seed });
};
function functor(F) {
    return function (lift, liftEq) {
        var arb = lift(fc.string());
        var Sa = liftEq(Eq_1.eqString);
        var Sc = liftEq(Eq_1.eqNumber);
        var identity = fc.property(arb, laws.functor.identity(F, Sa));
        var ab = function (s) { return (s.length === 1 ? undefined : s.length === 2 ? null : s.length); };
        var bc = function (n) { return (n === undefined ? 1 : n === null ? 2 : n * 2); };
        var composition = fc.property(arb, laws.functor.composition(F, Sc, ab, bc));
        fc.assert(identity);
        fc.assert(composition);
    };
}
exports.functor = functor;
function apply(F) {
    var functorF = functor(F);
    return function (lift, liftEq) {
        functorF(lift, liftEq);
        var Sc = liftEq(Eq_1.eqBoolean);
        var arbFa = lift(fc.string());
        var arbFab = lift(fc.constant(function (a) { return a.length; }));
        var arbFbc = lift(fc.constant(function (b) { return b > 2; }));
        var associativeComposition = fc.property(arbFa, arbFab, arbFbc, laws.apply.associativeComposition(F, Sc));
        fc.assert(associativeComposition);
    };
}
exports.apply = apply;
function applicative(F) {
    var applyF = apply(F);
    return function (lift, liftEq) {
        applyF(lift, liftEq);
        var arbFa = lift(fc.string());
        var Sa = liftEq(Eq_1.eqString);
        var Sb = liftEq(Eq_1.eqNumber);
        var identity = fc.property(arbFa, laws.applicative.identity(F, Sa));
        var ab = function (s) { return s.length; };
        var homomorphism = fc.property(fc.string(), laws.applicative.homomorphism(F, Sb, ab));
        var arbFab = lift(fc.constant(ab));
        var interchange = fc.property(fc.string(), arbFab, laws.applicative.interchange(F, Sb));
        var derivedMap = fc.property(arbFa, laws.applicative.derivedMap(F, Sb, ab));
        fc.assert(identity);
        fc.assert(homomorphism);
        fc.assert(interchange);
        fc.assert(derivedMap);
    };
}
exports.applicative = applicative;
function monad(M) {
    var applicativeM = applicative(M);
    return function (liftEq) {
        applicativeM(function (arb) { return arb.map(M.of); }, liftEq);
        var Sc = liftEq(Eq_1.eqBoolean);
        var arbFa = fc.string().map(M.of);
        var afb = function (s) { return M.of(s.length); };
        var bfc = function (n) { return M.of(n > 2); };
        var associativity = fc.property(arbFa, laws.chain.associativity(M, Sc, afb, bfc));
        var Sb = liftEq(Eq_1.eqNumber);
        var fab = M.of(function (a) { return a.length; });
        var derivedAp = fc.property(arbFa, laws.chain.derivedAp(M, Sb, fab));
        fc.assert(associativity);
        fc.assert(derivedAp);
        var arb = fc.string().map(M.of);
        var Sa = liftEq(Eq_1.eqString);
        var leftIdentity = fc.property(fc.string(), laws.monad.leftIdentity(M, Sb, afb));
        var rightIdentity = fc.property(arb, laws.monad.rightIdentity(M, Sa));
        var ab = function (s) { return s.length; };
        var derivedMap = fc.property(arb, laws.monad.derivedMap(M, Sb, ab));
        fc.assert(leftIdentity);
        fc.assert(rightIdentity);
        fc.assert(derivedMap);
    };
}
exports.monad = monad;
